import {
  Subject,
  SyllabusUnit,
  Chapter,
  Topic,
  PastPaper,
  StudentProfile,
  Goal,
  DEFAULT_PAST_PAPERS_SEED,
  SYLLABUS_CATALOG,
} from '@study-tracker/domain';
import { deterministicUUID } from '../utils/uuid';

const NOW = new Date().toISOString();
const DEFAULT_ORIGIN = 'system-seed';

export function createDefaultSeedData() {
  const subjects: Subject[] = SYLLABUS_CATALOG.map((catalog, index) => ({
    id: deterministicUUID(`SUBJECT_${catalog.code}`),
    code: catalog.code,
    name: catalog.name,
    colorToken: catalog.colorToken,
    sortOrder: index + 1,
    targetHours: catalog.code === 'COMBINED_MATHS' ? 350 : 300,
    isArchived: catalog.code === 'BIOLOGY' || catalog.code === 'ICT',
    createdAt: NOW,
    updatedAt: NOW,
    deletedAt: null,
    version: 1,
    originDeviceId: DEFAULT_ORIGIN,
    schemaVersion: 1,
  }));
  const subjectIdMap = Object.fromEntries(subjects.map((subject) => [subject.code, subject.id])) as Record<string, string>;
  const syllabusUnits: SyllabusUnit[] = SYLLABUS_CATALOG.flatMap((catalog) => catalog.units.map((title, index) => ({
    id: deterministicUUID(`UNIT_${catalog.code}_${index + 1}`),
    subjectId: subjectIdMap[catalog.code],
    unitNumber: index + 1,
    title,
    status: 'NOT_STARTED',
    sortOrder: index,
    createdAt: NOW,
    updatedAt: NOW,
    deletedAt: null,
    version: 1,
    originDeviceId: DEFAULT_ORIGIN,
    schemaVersion: 1,
  })));

  const mathsId = subjectIdMap.COMBINED_MATHS;
  const mathsUnits = syllabusUnits.filter((unit) => unit.subjectId === mathsId);
  const chapters: Chapter[] = [
    {
      id: deterministicUUID('CHAP_DIFF'),
      unitId: mathsUnits[8].id,
      chapterNumber: 1,
      title: 'Differentiation & Applications',
      sortOrder: 1,
      createdAt: NOW,
      updatedAt: NOW,
      deletedAt: null,
      version: 1,
      originDeviceId: DEFAULT_ORIGIN,
      schemaVersion: 1,
    },
    {
      id: deterministicUUID('CHAP_INTEG'),
      unitId: mathsUnits[9].id,
      chapterNumber: 1,
      title: 'Integration & Definite Integrals',
      sortOrder: 1,
      createdAt: NOW,
      updatedAt: NOW,
      deletedAt: null,
      version: 1,
      originDeviceId: DEFAULT_ORIGIN,
      schemaVersion: 1,
    },
  ];

  const topics: Topic[] = [
    {
      id: deterministicUUID('TOPIC_CHAIN_RULE'),
      chapterId: chapters[0].id,
      topicNumber: 1,
      title: 'Chain Rule & Parametric Differentiation',
      status: 'NOT_STARTED',
      masteryScore: 0,
      notes: '',
      sortOrder: 1,
      createdAt: NOW,
      updatedAt: NOW,
      deletedAt: null,
      version: 1,
      originDeviceId: DEFAULT_ORIGIN,
      schemaVersion: 1,
    },
    {
      id: deterministicUUID('TOPIC_INTEG_PARTS'),
      chapterId: chapters[1].id,
      topicNumber: 1,
      title: 'Integration by Parts & Partial Fractions',
      status: 'NOT_STARTED',
      masteryScore: 0,
      notes: '',
      sortOrder: 1,
      createdAt: NOW,
      updatedAt: NOW,
      deletedAt: null,
      version: 1,
      originDeviceId: DEFAULT_ORIGIN,
      schemaVersion: 1,
    },
  ];

  // 4. Past Papers (17 years x 2 papers x 3 subjects = 102 items)
  const pastPapers: PastPaper[] = DEFAULT_PAST_PAPERS_SEED.map((seed) => ({
    id: deterministicUUID(`PAST_PAPER_${seed.subjectCode}_${seed.year}_${seed.paperType}`),
    subjectId: subjectIdMap[seed.subjectCode],
    year: seed.year,
    paperType: seed.paperType,
    status: 'NOT_STARTED',
    difficulty: null,
    isRevisionRequired: false,
    score: null,
    notes: '',
    fileAttachmentUri: null,
    createdAt: NOW,
    updatedAt: NOW,
    deletedAt: null,
    version: 1,
    originDeviceId: DEFAULT_ORIGIN,
    schemaVersion: 1,
  }));

  // 5. Default Student Profile
  const studentProfile: StudentProfile = {
    id: deterministicUUID('PROFILE_DEFAULT'),
    candidateId: '',
    fullName: '',
    stream: 'G.C.E. Advanced Level',
    targetYear: 2026,
    targetZScore: 2.85,
    targetDistrictRank: 10,
    bio: 'G.C.E. Advanced Level student',
    avatarUrl: null,
    createdAt: NOW,
    updatedAt: NOW,
    deletedAt: null,
    version: 1,
    originDeviceId: DEFAULT_ORIGIN,
    schemaVersion: 1,
  };

  // 6. Default Goal
  const goal: Goal = {
    id: deterministicUUID('GOAL_PRIMARY'),
    title: 'Daily Velocity 6.0 Hours',
    targetHoursPerDay: 6.0,
    targetHoursPerWeek: 36.0,
    targetSyllabusPercentage: 100,
    targetDate: '2026-08-01T00:00:00Z',
    isCompleted: false,
    createdAt: NOW,
    updatedAt: NOW,
    deletedAt: null,
    version: 1,
    originDeviceId: DEFAULT_ORIGIN,
    schemaVersion: 1,
  };

  return {
    subjects,
    syllabusUnits,
    chapters,
    topics,
    pastPapers,
    studentProfile,
    goal,
  };
}
